/**
 * Aceternity UI — Carousel (adapted for Vite + framer-motion + lucide)
 * https://ui.aceternity.com/components/carousel
 *
 * Infinite loop: track is [lastClone, ...slides, firstClone]. Advancing past the last
 * slide animates onto a clone of the first, then the track silently rewinds to the real
 * first — same pixels, no “restart” of title/orb.
 */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PlanYourVisitOrbButton } from './PlanYourVisitOrbButton';
import './carousel-flesh-btn.css';

/** One slide stride: card width + horizontal margins (must match Slide classes). */
const SLIDE_STRIDE = 'calc(70vmin + 8vmin)';

export type AceternitySlideData = {
  title: string;
  button: string;
  src: string;
  onButtonClick?: () => void;
};

type TrackSlide = AceternitySlideData & { trackKey: string; logicalIndex: number };

type SlideProps = {
  slide: TrackSlide;
  index: number;
  current: number;
  handleSlideClick: (index: number) => void;
  /** Off during silent loop jumps so scale/opacity don’t “restart”. */
  tween: boolean;
};

function Slide({ slide, index, current, handleSlideClick, tween }: SlideProps) {
  const slideRef = useRef<HTMLLIElement>(null);
  const xRef = useRef(0);
  const yRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const animate = () => {
      if (!slideRef.current) return;
      slideRef.current.style.setProperty('--x', `${xRef.current}px`);
      slideRef.current.style.setProperty('--y', `${yRef.current}px`);
      frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    const el = slideRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    xRef.current = e.clientX - (r.left + r.width / 2);
    yRef.current = e.clientY - (r.top + r.height / 2);
  };

  const handleMouseLeave = () => {
    xRef.current = 0;
    yRef.current = 0;
  };

  const active = current === index;

  return (
    <li
      ref={slideRef}
      className={cn(
        'relative mx-[4vmin] flex h-[70vmin] w-[70vmin] shrink-0 flex-col items-center justify-center text-center text-white opacity-100',
        tween && 'transition-[opacity,transform] duration-300 ease-in-out',
      )}
      onClick={() => handleSlideClick(index)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transformOrigin: 'bottom',
        opacity: active ? 1 : 0.5,
        transform: active ? 'scale(1)' : 'scale(0.98)',
        zIndex: active ? 20 : 10,
      }}
    >
      <div
        className="absolute top-0 left-0 h-full w-full overflow-hidden rounded-2xl bg-[#1D1F2F] transition-all duration-150 ease-out"
        style={{
          transform: active
            ? 'translate3d(calc(var(--x) / 30), calc(var(--y) / 30), 0)'
            : 'none',
        }}
      >
        <img
          className="absolute inset-0 h-[120%] w-[120%] object-cover opacity-100 transition-opacity duration-500 ease-in-out"
          style={{ opacity: active ? 1 : 0.5 }}
          alt={slide.title}
          src={slide.src}
          loading="eager"
          decoding="async"
          draggable={false}
        />
        {active ? <div className="absolute inset-0 bg-black/30 transition-all duration-700" /> : null}
      </div>
    </li>
  );
}

function CarouselControl({
  type,
  title,
  handleClick,
}: {
  type: 'previous' | 'next';
  title: string;
  handleClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#3d2817]/15 bg-white/90 text-[#3d2817] shadow-md transition duration-200 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2f9e44]',
        type === 'previous' && 'rotate-180',
      )}
      title={title}
      aria-label={title}
      onClick={handleClick}
    >
      <ArrowRight className="h-5 w-5" aria-hidden />
    </button>
  );
}

/** Small transparent triple-chevron cue in the card gutters */
function FleshCueButton({
  side,
  label,
  onClick,
}: {
  side: 'prev' | 'next';
  label: string;
  onClick: () => void;
}) {
  return (
    <div
      className={cn(
        'carousel-flesh-btn absolute top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
        side === 'prev' && 'carousel-flesh-btn--prev',
      )}
      style={{ left: side === 'next' ? 'calc(100% + 4vmin)' : 'calc(0% - 4vmin)' }}
    >
      <div className="carousel-flesh-btn__mirror">
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className="carousel-flesh-btn__content"
        >
          <svg
            className="carousel-flesh-btn__arrow"
            width="66"
            height="43"
            viewBox="0 0 66 43"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              className="carousel-flesh-btn__arrow-path carousel-flesh-btn__arrow-one"
              d="M40.1543933,3.89485454 L43.9763149,0.139296592 C44.1708311,-0.0518420739 44.4826329,-0.0518571125 44.6771675,0.139262789 L65.6916134,20.7848311 C66.0855801,21.1718824 66.0911863,21.8050225 65.704135,22.1989893 C65.7000188,22.2031791 65.6958657,22.2073326 65.6916762,22.2114492 L44.677098,42.8607841 C44.4825957,43.0519059 44.1708242,43.0519358 43.9762853,42.8608513 L40.1545186,39.1069479 C39.9575152,38.9134427 39.9546793,38.5968729 40.1481845,38.3998695 C40.1502893,38.3977268 40.1524132,38.395603 40.1545562,38.3934985 L56.9937789,21.8567812 C57.1908028,21.6632968 57.193672,21.3467273 57.0001876,21.1497035 C56.9980647,21.1475418 56.9959223,21.1453995 56.9937605,21.1432767 L40.1545208,4.60825197 C39.9574869,4.41477773 39.9546013,4.09820839 40.1480756,3.90117456 C40.1501626,3.89904911 40.1522686,3.89694235 40.1543933,3.89485454 Z"
            />
            <path
              className="carousel-flesh-btn__arrow-path carousel-flesh-btn__arrow-two"
              d="M20.1543933,3.89485454 L23.9763149,0.139296592 C24.1708311,-0.0518420739 24.4826329,-0.0518571125 24.6771675,0.139262789 L45.6916134,20.7848311 C46.0855801,21.1718824 46.0911863,21.8050225 45.704135,22.1989893 C45.7000188,22.2031791 45.6958657,22.2073326 45.6916762,22.2114492 L24.677098,42.8607841 C24.4825957,43.0519059 24.1708242,43.0519358 23.9762853,42.8608513 L20.1545186,39.1069479 C19.9575152,38.9134427 19.9546793,38.5968729 20.1481845,38.3998695 C20.1502893,38.3977268 20.1524132,38.395603 20.1545562,38.3934985 L36.9937789,21.8567812 C37.1908028,21.6632968 37.193672,21.3467273 37.0001876,21.1497035 C36.9980647,21.1475418 36.9959223,21.1453995 36.9937605,21.1432767 L20.1545208,4.60825197 C19.9574869,4.41477773 19.9546013,4.09820839 20.1480756,3.90117456 C20.1501626,3.89904911 20.1522686,3.89694235 20.1543933,3.89485454 Z"
            />
            <path
              className="carousel-flesh-btn__arrow-path carousel-flesh-btn__arrow-three"
              d="M0.154393339,3.89485454 L3.97631488,0.139296592 C4.17083111,-0.0518420739 4.48263286,-0.0518571125 4.67716753,0.139262789 L25.6916134,20.7848311 C26.0855801,21.1718824 26.0911863,21.8050225 25.704135,22.1989893 C25.7000188,22.2031791 25.6958657,22.2073326 25.6916762,22.2114492 L4.67709797,42.8607841 C4.48259567,43.0519059 4.17082418,43.0519358 3.97628526,42.8608513 L0.154518591,39.1069479 C-0.0424848215,38.9134427 -0.0453206733,38.5968729 0.148184538,38.3998695 C0.150289256,38.3977268 0.152413239,38.395603 0.154556228,38.3934985 L16.9937789,21.8567812 C17.1908028,21.6632968 17.193672,21.3467273 17.0001876,21.1497035 C16.9980647,21.1475418 16.9959223,21.1453995 16.9937605,21.1432767 L0.15452076,4.60825197 C-0.0425130651,4.41477773 -0.0453986756,4.09820839 0.148075568,3.90117456 C0.150162624,3.89904911 0.152268631,3.89694235 0.154393339,3.89485454 Z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function AceternityCarousel({
  slides,
  className,
  planVisitOrb = false,
  nextHintLabel = 'Next',
  prevHintLabel = 'Previous',
}: {
  slides: AceternitySlideData[];
  className?: string;
  /** Use circular rotating Plan your visit CTA instead of a pill button. */
  planVisitOrb?: boolean;
  nextHintLabel?: string;
  prevHintLabel?: string;
}) {
  const id = useId();
  const trackRef = useRef<HTMLUListElement>(null);
  const [animateTrack, setAnimateTrack] = useState(true);
  /** Hide flesh cues while the track is animating between pictures. */
  const [isSliding, setIsSliding] = useState(false);

  const hasMultiple = slides.length > 1;

  /**
   * Circular track: [clone(last), ...real slides, clone(first)]
   * Real slides occupy indices 1..n; start on index 1.
   */
  const trackSlides: TrackSlide[] = useMemo(() => {
    if (!slides.length) return [];
    if (!hasMultiple) {
      return slides.map((s, i) => ({ ...s, trackKey: `solo-${i}`, logicalIndex: i }));
    }
    const n = slides.length;
    const last = slides[n - 1]!;
    const first = slides[0]!;
    return [
      { ...last, trackKey: `clone-last-${last.src}`, logicalIndex: n - 1 },
      ...slides.map((s, i) => ({ ...s, trackKey: `real-${i}-${s.src}`, logicalIndex: i })),
      { ...first, trackKey: `clone-first-${first.src}`, logicalIndex: 0 },
    ];
  }, [slides, hasMultiple]);

  const realCount = slides.length;
  const cloneLastIndex = 0;
  const realFirstIndex = 1;
  const realLastIndex = realCount;
  const cloneFirstIndex = realCount + 1;

  const [current, setCurrent] = useState(() => (hasMultiple ? realFirstIndex : 0));
  const currentRef = useRef(current);
  currentRef.current = current;

  const logicalIndex = hasMultiple
    ? current === cloneLastIndex
      ? realCount - 1
      : current === cloneFirstIndex
        ? 0
        : current - 1
    : current;

  const activeSlide = slides[logicalIndex] ?? slides[0];

  if (!slides.length || !activeSlide) return null;

  const silentJumpTo = (next: number) => {
    const el = trackRef.current;
    setAnimateTrack(false);
    if (el) el.style.transition = 'none';
    setCurrent(next);
    currentRef.current = next;
    void el?.offsetHeight;
    requestAnimationFrame(() => {
      setAnimateTrack(true);
      if (el) el.style.transition = '';
      setIsSliding(false);
    });
  };

  const goTo = (next: number, withAnimation: boolean) => {
    if (withAnimation) setIsSliding(true);
    setAnimateTrack(withAnimation);
    setCurrent(next);
    currentRef.current = next;
  };

  const handlePreviousClick = () => {
    if (!hasMultiple || isSliding) return;
    if (current <= cloneLastIndex) return;
    goTo(current - 1, true);
  };

  const handleNextClick = () => {
    if (!hasMultiple || isSliding) return;
    if (current >= cloneFirstIndex) return;
    goTo(current + 1, true);
  };

  const handleSlideClick = (index: number) => {
    if (index === current) return;
    if (hasMultiple && (index === cloneFirstIndex || index === cloneLastIndex)) {
      if (index === cloneFirstIndex) handleNextClick();
      else handlePreviousClick();
      return;
    }
    goTo(index, true);
  };

  const onTrackTransitionEnd = (e: React.TransitionEvent<HTMLUListElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform') return;
    if (!hasMultiple) return;
    const at = currentRef.current;
    // Landed on clone(first) after a forward step → teleport to real first (identical pixels).
    if (at === cloneFirstIndex) {
      silentJumpTo(realFirstIndex);
      return;
    }
    // Landed on clone(last) after a backward step → teleport to real last.
    if (at === cloneLastIndex) {
      silentJumpTo(realLastIndex);
      return;
    }
    setIsSliding(false);
  };

  return (
    <div
      className={cn('relative mx-auto h-[70vmin] w-[70vmin]', className)}
      aria-labelledby={`carousel-heading-${id}`}
    >
      <ul
        ref={trackRef}
        className={cn(
          'absolute top-0 left-0 mx-[-4vmin] flex ease-in-out',
          animateTrack ? 'transition-transform duration-700' : 'transition-none',
        )}
        style={{
          transform: `translate3d(calc(-1 * ${current} * (${SLIDE_STRIDE})), 0, 0)`,
        }}
        onTransitionEnd={onTrackTransitionEnd}
      >
        {trackSlides.map((slide, index) => (
          <Slide
            key={slide.trackKey}
            slide={slide}
            index={index}
            current={current}
            handleSlideClick={handleSlideClick}
            tween={animateTrack}
          />
        ))}
      </ul>

      {/* Center chrome keyed by logical slide src — clone→real snap keeps the same key. */}
      <div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center p-[4vmin]">
        <AnimatePresence mode="sync">
          <motion.div
            key={activeSlide.src}
            className="flex flex-col items-center justify-center text-center"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
          >
            <h2
              id={`carousel-heading-${id}`}
              className="relative text-3xl font-semibold text-white uppercase md:text-4xl lg:text-5xl"
              style={{ fontFamily: '"Felix Titling", "Times New Roman", serif' }}
            >
              {activeSlide.title}
            </h2>
          </motion.div>
        </AnimatePresence>
      </div>

      {planVisitOrb ? (
        <div className="pointer-events-auto absolute right-4 bottom-4 z-40 md:right-5 md:bottom-5">
          <PlanYourVisitOrbButton
            label={activeSlide.button}
            onClick={() => activeSlide.onButtonClick?.()}
          />
        </div>
      ) : (
        <div className="pointer-events-auto absolute bottom-8 left-1/2 z-40 -translate-x-1/2">
          <button
            type="button"
            className="flex h-12 w-fit items-center justify-center rounded-2xl bg-[#2f9e44] px-6 text-sm font-semibold text-white transition duration-200 hover:bg-[#27863a]"
            onClick={() => activeSlide.onButtonClick?.()}
          >
            {activeSlide.button}
          </button>
        </div>
      )}

      {/* Flesh cues in the gutters between cards — hidden while slides are moving */}
      {hasMultiple && !isSliding ? (
        <>
          <FleshCueButton side="prev" label={prevHintLabel} onClick={handlePreviousClick} />
          <FleshCueButton side="next" label={nextHintLabel} onClick={handleNextClick} />
        </>
      ) : null}

      <div className="absolute top-[calc(100%+1rem)] flex w-full justify-center gap-3">
        <CarouselControl
          type="previous"
          title="Go to previous slide"
          handleClick={handlePreviousClick}
        />
        <CarouselControl type="next" title="Go to next slide" handleClick={handleNextClick} />
      </div>

      <span className="sr-only" aria-live="polite">
        {activeSlide.title}
      </span>
    </div>
  );
}
